<template>
    <div class="listing-layout flex-column hp-10">
        <!-- HEADER -->
        <header class="flex-level mb-4">
            <!-- TITLE -->
            <h1 v-if="$slots['header'] != '' || metaTitle != ''" class="m-0">
                <slot name="header">{{ metaTitle }}</slot>
                <CompassSelect
                    v-if="funnelFilter"
                    v-model="funnelFilter.data.choice"
                    @update:value="(value) => updateFunnelFilter(value)"
                >
                    <CompassDropdownSelectOption
                        v-for="(pipe, index) in funnelList"
                        :key="index"
                        :value="pipe.value"
                        :label="pipe.label"
                    />
                </CompassSelect>

                <span class="fs-3"
                    >({{
                        $l10n.n__('%(nb)s opportunité', '%(nb)s opportunités', totalOpp, {
                            nb: totalOpp,
                        })
                    }})</span
                >
            </h1>

            <!-- ACTIONS -->
            <div class="flex-right">
                <ul class="action-nav">
                    <li v-for="button in primaryButtons" :key="button.label">
                        <el-tooltip
                            :content="$t.__('common.labels.no_necessary_rights')"
                            placement="top"
                            :disabled="!button.disabled"
                        >
                            <CompassButton
                                v-if="button.event != undefined"
                                :label="button.label"
                                :disabled="button.disabled"
                                @click="$emit(button.event)"
                            />
                            <CompassButton
                                v-else
                                :label="button.label"
                                :disabled="button.disabled"
                                @click="hrefButton(button.href)"
                            />
                        </el-tooltip>
                    </li>
                    <li v-if="secondaryButtons.length > 0">
                        <el-dropdown placement="bottom-end">
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item
                                    v-for="button in secondaryButtons"
                                    :key="button.label"
                                    :disabled="button.disabled"
                                >
                                    <a
                                        v-if="button.event != undefined"
                                        class="black-link block nowrap"
                                        @click="$emit(button.event)"
                                    >
                                        {{ button.label }}
                                    </a>
                                    <a
                                        v-else
                                        :href="button.href"
                                        class="black-link block nowrap"
                                        @click="button.click != undefined ? button.click : ''"
                                    >
                                        {{ button.label }}
                                    </a>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </li>
                </ul>
            </div>
        </header>
        <!-- HEADER END -->

        <!-- BODY -->
        <div class="listing prelative flex-column mh-0 flex">
            <!-- LISTING TOOLS -->
            <div class="listing-tools flex-level flex-top no-shrink mb-2">
                <!-- FILTER -->
                <div class="flex flex-level flex-top">
                    <CompassButton
                        v-if="enableFiltering"
                        :label="$t.__('common.actions.filter')"
                        @click.prevent="$store.dispatch(`${listinghash}/toggleDisplay`, 'filters')"
                    />
                    <filters-preview v-if="!display.filters" :listinghash="listinghash" />
                </div>

                <!-- OPTIONS -->
                <CompassToggleButtonGroup
                    :value="activeToggleButton"
                    exclusive
                    size="small"
                    class="ml-2"
                >
                    <CompassTooltip
                        v-for="(mode, index) in displayModes"
                        :key="mode.class"
                        :content="mode.label"
                        :arrow="false"
                    >
                        <CompassToggleButton
                            :value="mode.class"
                            :icon="mode.icon"
                            @click.native="setDisplayMode(mode.class, index)"
                        />
                    </CompassTooltip>
                </CompassToggleButtonGroup>
            </div>
            <!-- LISTING TOOLS END -->

            <!-- LISTING & FILTER PANEL -->
            <div class="flex-row mh-0 flex">
                <!-- FILTER PANEL -->
                <div
                    v-show="display.filters && enableFiltering"
                    class="leftpane well well-no-shadow mr-4 flex-column"
                >
                    <filters-pane :listinghash="listinghash" />
                </div>

                <!-- COLUMN PANEL -->
                <transition name="slide-fade">
                    <listing-columns v-if="display.columns" :listinghash="listinghash" />
                </transition>

                <!-- DISPLAY OPTION PANEL -->
                <transition name="slide-fade">
                    <listing-display
                        v-if="display.table"
                        :listinghash="listinghash"
                        :show-display-mode="true"
                        :show-display-pagination="false"
                    />
                </transition>

                <!-- LISTING -->
                <div class="listingContainer flex flex-column">
                    <div class="listingContent prelative ov-auto">
                        <!-- EMPTY RESULT -->
                        <div
                            v-if="!error && !loading && Object.keys(rows).length == 0"
                            class="placeholder"
                        >
                            <p class="acenter">
                                {{ $l10n.__('Aucun résultat ne correspond aux filtres appliqués') }}
                            </p>
                            <CompassButton
                                :label="$t.__('common.actions.reset')"
                                @click="$store.dispatch(`${listinghash}/resetListing`)"
                            />
                        </div>

                        <pipeline :listinghash="listinghash"></pipeline>
                    </div>
                </div>
                <!-- LISTING END -->
            </div>
            <!-- LISTING  & FILTER PANEL END -->

            <listing-favoritesform
                v-if="display.favoritesForm"
                :listinghash="listinghash"
                :favorite-data="favoriteData"
                :has-layout="false"
            />

            <export-modal
                v-if="display.export"
                :listinghash="listinghash"
                :export-type="display.exportType"
            />
        </div>
        <!-- BODY END -->
    </div>
</template>

<script>
import FiltersPane from './FiltersPane.vue';
import FiltersPreview from './FiltersPreview.vue';
import Paginator from '../utilities/Paginator.vue';
import ListingFavoritesform from './favorites/ListingFavoriteForm.vue';
import ListingColumns from './ListingColumns.vue';
import ListingDisplay from './ListingDisplay.vue';
import ListingModule from 'vueDir/store/pipeline';
import ExportModal from './ExportModal.vue';
import Pipeline from './Pipeline.vue';
import { guid } from 'vueDir/helpers';
import _ from 'lodash';
import { goToUrl } from 'vueDir/helpers';

export default {
    components: {
        FiltersPane,
        Paginator,
        FiltersPreview,
        ListingColumns,
        ListingDisplay,
        ExportModal,
        ListingFavoritesform,
        Pipeline,
    },

    events: {
        'listing:refresh': 'fetch',
    },

    props: {
        url: {
            type: String,
            required: true,
        },
        buttons: {
            required: false,
            type: Array,
            default: [],
        },
        params: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        linkedtype: {
            type: String,
        },
        prefilter: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        enableFiltering: {
            type: Boolean,
            required: false,
            default: true,
        },
        favorite: {
            required: false,
            type: Number,
            default: null,
        },
        name: {
            required: false,
            type: String,
            default: '',
        },
        funnel: {
            required: true,
            type: Object,
        },
        useFavorites: {
            type: Boolean,
            default: true,
        },
    },

    data: function () {
        return {
            listinghash: '',
            displayModes: [
                {
                    label: this.$l10n.__('Vue condensée'),
                    class: 'condensed',
                    icon: 'condensed-fill',
                },
                {
                    label: this.$l10n.__('Vue étendue'),
                    class: 'standard',
                    icon: 'extended-fill',
                },
            ],
            activeToggleButton: 'condensed',
        };
    },

    computed: {
        primaryButtons() {
            let primaryButtons = this.buttons.filter((button) => {
                return button.primary;
            });

            if (primaryButtons.length) {
                return primaryButtons;
            }

            return [];
        },

        secondaryButtons() {
            let secondaryButtons = this.buttons.filter((button) => {
                return button.primary == undefined || !button.primary;
            });

            if (secondaryButtons && secondaryButtons.length) {
                return secondaryButtons;
            }

            return [];
        },

        metaTitle() {
            return this.$route.meta && this.$route.meta.title != ''
                ? this.$route.meta.title
                : false;
        },

        funnelFilter() {
            let index = _.findIndex(this.$store.state[this.listinghash].filters, {
                name: 'funnelid',
            });

            if (index != -1) {
                return this.$store.state[this.listinghash].filters[index];
            }

            return false;
        },

        funnelList() {
            if (this.funnelFilter) {
                let options = { ...this.funnelFilter.inputs.choice.options };

                return Object.values(options);
            }

            return false;
        },
        ajax() {
            return this.$store.state[this.listinghash].ajax;
        },

        error() {
            return this.$store.state[this.listinghash].error;
        },

        init() {
            return this.$store.state[this.listinghash].init;
        },

        rows() {
            return this.$store.state[this.listinghash].rows;
        },

        pagination() {
            return this.$store.state[this.listinghash].pagination;
        },
        columns() {
            return this.$store.state[this.listinghash].columns;
        },
        display() {
            return this.$store.state[this.listinghash].display;
        },

        activeColumns() {
            return this.$store.getters[`${this.listinghash}/activeColumns`];
        },

        steps() {
            return this.$store.getters[`${this.listinghash}/steps`];
        },

        activeFilters() {
            return this.$store.getters[`${this.listinghash}/activeFilters`];
        },

        loading() {
            let loading = !!this.ajax;

            for (let stepid in this.$store.state[this.listinghash].ajaxStep) {
                if (!!this.$store.state[this.listinghash].ajaxStep[stepid]) {
                    return true;
                }
            }

            return loading;
        },

        meta() {
            return this.$store.getters[`${this.listinghash}/meta`];
        },

        permissions() {
            return this.$store.state[this.listinghash].permissions;
        },

        favoriteData() {
            return this.$store.state[this.listinghash].favoriteData;
        },

        totalOpp() {
            return this.$store.getters[`${this.listinghash}/totalOpp`];
        },
    },

    created() {
        // Dynamically require state
        this.listinghash = this.name + '_' + guid();
        const store = this.$store;

        if (!(store && store.state && store.state[this.listinghash])) {
            store.registerModule(this.listinghash, ListingModule);
        } else {
            // re-use the already existing module
            console.log(`reusing module: ${this.listinghash}`);
        }

        // Listing init
        this.$store.dispatch(`${this.listinghash}/init`, { ...this.$props, favorite: this.$route.query.favorite }).then(() => this.fetch());

        // Event when step load is finished
        window.EventBus.$on('pipeline:endRefreshData', () => {
            this.checkPermissionButtons();
        });

        this.$store.commit(`${this.listinghash}/setDefaultFunnel`, this.funnel);
    },

    methods: {
        // PAGINATION
        goToPage(page) {
            this.$store.commit(`${this.listinghash}/updateCurrentPage`, page);
            this.fetch();
        },

        // Main methods
        fetch(overwriteMeta) {
            this.$store.dispatch(`${this.listinghash}/fetch`, overwriteMeta);
            this.$store.dispatch(`${this.listinghash}/fetchFavorites`);
        },

        checkPermissionButtons() {
            this.buttons.map((button) => {
                if (button.permission && button.permission != '') {
                    button.disabled =
                        this.permissions[button.permission] &&
                        this.permissions[button.permission] == 1
                            ? false
                            : true;
                } else {
                    button.disabled = false;
                }
            });
        },

        updateFunnelFilter(newValue) {
            let filter = { ...this.funnelFilter };
            filter.data.choice = newValue;

            this.$store.commit(`${this.listinghash}/applyFilter`, filter);
            this.fetch();
        },

        getDisplayMode() {
            return this.$store.state[this.listinghash].display.density;
        },

        setDisplayMode(value, index) {
            this.$store.commit(`${this.listinghash}/updateDisplay`, { density: value });
            this.$store.dispatch(`${this.listinghash}/updateDisplay`);
            this.activeToggleButton = value;
        },

        isDisplayModeSelected(display) {
            return this.getDisplayMode() === display;
        },
        hrefButton(url) {
            return goToUrl(url);
        },
    },
};
</script>

<style lang="sass" scoped>
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';
@import '~@sellsy/styleguide/src/_transitions';

.flex-row {
    .leftpane{
        width : 280px;
    }
}

.listingContent{
    display:flex;
    flex:1;
}
</style>
